/**
 * Scheduled user attestations (CUR-858).
 *
 * A schedule sends a user attestation on a chosen day, once or on a cadence,
 * exactly as a manual send would. These constants are shared so the browser
 * gets labels and bounds without importing anything server-side.
 */

/** PostHog flag gating the whole feature — server procedures, sweep and client routes. */
export const SCHEDULED_USER_ATTESTATION_FLAG = 'enable-scheduled-attestations';

/**
 * How often a schedule re-sends, with the labels shown in the UI. Mirrors the
 * `ScheduledUserAttestationFrequency` Prisma enum. Order is the dropdown order
 * the ticket specifies.
 */
export const SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    FORTNIGHTLY: 'Fortnightly',
    MONTHLY: 'Monthly',
    EVERY_3_MONTHS: 'Every 3 months',
    EVERY_6_MONTHS: 'Every 6 months',
    ANNUALLY: 'Annually',
} as const satisfies Record<string, string>;

export type ScheduledUserAttestationFrequencyKey = keyof typeof SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS;

export const SCHEDULED_USER_ATTESTATION_FREQUENCIES = Object.keys(
    SCHEDULED_USER_ATTESTATION_FREQUENCY_LABELS,
) as readonly ScheduledUserAttestationFrequencyKey[];

/** Register status labels. Mirrors the `ScheduledUserAttestationStatus` Prisma enum. */
export const SCHEDULED_USER_ATTESTATION_STATUS_LABELS = {
    ACTIVE: 'Active',
    DISABLED: 'Disabled',
    COMPLETED: 'Completed',
} as const satisfies Record<string, string>;

export type ScheduledUserAttestationStatusKey = keyof typeof SCHEDULED_USER_ATTESTATION_STATUS_LABELS;

/** Who a schedule targets. Mirrors the `ScheduledUserAttestationRecipientMode` Prisma enum. */
export const SCHEDULED_USER_ATTESTATION_RECIPIENT_MODES = ['USERS', 'HANDLING_PARTIES', 'ALL_ACTIVE'] as const;

export type ScheduledUserAttestationRecipientModeKey = (typeof SCHEDULED_USER_ATTESTATION_RECIPIENT_MODES)[number];

export const SCHEDULED_USER_ATTESTATION_LIMITS = {
    /** Explicitly targeted users (USERS mode). */
    MAX_TARGET_USERS: 500,
    /** Targeted handling parties (HANDLING_PARTIES mode). */
    MAX_TARGET_HANDLING_PARTIES: 100,
    /** Per-user exclusions (HANDLING_PARTIES / ALL_ACTIVE modes). */
    MAX_EXCLUDED_USERS: 500,
    /**
     * Soft per-organisation cap. Schedules are deletable config, so this is a
     * sanity bound on list and sweep cost rather than a budget.
     */
    MAX_SCHEDULES_PER_ORG: 200,
    /** Minutes from local midnight at which every occurrence fires (09:00). */
    SEND_MINUTE_OF_DAY: 9 * 60,
} as const;

/**
 * Attestation reporting: the Overview page, round pages, the person record,
 * the coverage check, bulk reminders and due-date extensions, and the weekly
 * digest. One PostHog flag gates all of it — server procedures, the digest
 * job and the client routes and controls.
 */
export const ATTESTATION_REPORTING_FLAG = 'enable-attestation-reporting';

export const ATTESTATION_REPORTING_LIMITS = {
    /** Attestations one bulk action (remind, extend, cancel, pass) may touch. */
    MAX_BULK_IDS: 500,
    /** A person is reminded about one attestation at most once in this many hours. */
    MANUAL_REMINDER_COOLDOWN_HOURS: 24,
    /** The note a sender can add to a manual reminder. */
    MAX_REMINDER_NOTE_LENGTH: 1000,
    /** The furthest a due date can be moved, in days from today. */
    MAX_EXTEND_DAYS: 365,
    /** Rows on the Overview's "Longest overdue" card. */
    LONGEST_OVERDUE_ROWS: 5,
    /** Attestations listed on the person record. */
    RECIPIENT_RECORD_ROWS: 50,
    /** Rounds a single page of the rounds list returns. */
    MAX_ROUNDS_PAGE_SIZE: 100,
} as const;

/** Trend periods the Overview offers, in days. */
export const ATTESTATION_REPORTING_PERIODS = [30, 90, 180, 365] as const;

export type AttestationReportingPeriod = (typeof ATTESTATION_REPORTING_PERIODS)[number];

/** Which rounds a list shows: those still in progress, those finished, or both. */
export const ATTESTATION_ROUND_STATES = ['OPEN', 'CLOSED', 'ALL'] as const;

export type AttestationRoundState = (typeof ATTESTATION_ROUND_STATES)[number];

/** Windows for the coverage check: who has not been sent a form since when. */
export const ATTESTATION_COVERAGE_PERIODS = {
    FINANCIAL_YEAR: 'This financial year',
    CALENDAR_YEAR: 'This calendar year',
    LAST_12_MONTHS: 'Last 12 months',
} as const satisfies Record<string, string>;

export type AttestationCoveragePeriod = keyof typeof ATTESTATION_COVERAGE_PERIODS;

/** Why a manual reminder was not sent for an attestation. */
export const ATTESTATION_REMINDER_SKIP_REASONS = {
    NOT_OUTSTANDING: 'Already submitted or cancelled',
    INACTIVE_RECIPIENT: 'The recipient is no longer an active member',
    LOCKED: 'The form does not allow submission after the due date — set a new due date first',
    RECENTLY_REMINDED: 'Reminded in the last 24 hours',
} as const satisfies Record<string, string>;

export type AttestationReminderSkipReason = keyof typeof ATTESTATION_REMINDER_SKIP_REASONS;
